
import { firestore, storage } from "./config";
import { 
  doc, getDoc, setDoc, collection, getDocs, deleteDoc, 
  onSnapshot, query, where, orderBy, limit, collectionGroup,
  updateDoc, writeBatch, serverTimestamp, Timestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { todayStr, nowTime, minutesToHHMM, HHMMToMinutes, getWorkingDaysCount, getLastNWorkingDays } from "../utils/helpers";

// ── HELPERS ───────────────────────────────────────

export function getCollection(idOrRole) {
  if (idOrRole === "staff" || (typeof idOrRole === "string" && idOrRole.startsWith("STAFF-"))) {
    return "staff";
  }
  return "students";
}

// ── STUDENT/STAFF PROFILE (Firestore Only) ──────────

export async function saveStudentProfile(studentId, profileData) {
  const coll = getCollection(studentId);
  const dataToSave = { ...profileData };

  // Flatten logic: if profileData contains a 'profile' object, spread it out.
  // This helps clean up any legacy data being re-saved.
  if (dataToSave.profile) {
    Object.assign(dataToSave, dataToSave.profile);
    delete dataToSave.profile;
  }

  // Ensure Firebase Auth UID is stored if available
  if (profileData.uid) { // profileData might come from AuthContext.user
    dataToSave.firebaseAuthUid = profileData.uid;
  }

  // Firestore does NOT support nested arrays. 
  // faceDescriptors is often a number[][] which must be stringified.
  if (dataToSave.faceDescriptors && Array.isArray(dataToSave.faceDescriptors)) {
    try {
      dataToSave.faceDescriptors = JSON.stringify(dataToSave.faceDescriptors.map(d => 
        d instanceof Float32Array || d instanceof Array ? Array.from(d) : d
      ));
    } catch (e) {
      console.error("Error serializing faceDescriptors in saveStudentProfile", e);
      delete dataToSave.faceDescriptors; // Prevent crash if serialization fails
    }
  }

  await setDoc(doc(firestore, coll, studentId), {
    ...dataToSave,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

export async function getStudent(studentId) {
  let coll = getCollection(studentId);
  let snap = await getDoc(doc(firestore, coll, studentId));
  
  // If not found in the guessed collection, try the other one
  if (!snap.exists()) {
    const otherColl = coll === "staff" ? "students" : "staff";
    const otherSnap = await getDoc(doc(firestore, otherColl, studentId));
    if (otherSnap.exists()) {
      snap = otherSnap;
      coll = otherColl;
    }
  }

  if (!snap.exists()) return null;
  
  const data = snap.data();
  
  // Flatten legacy 'profile' sub-object if it exists
  const legacyProfile = data.profile || {};
  const flattened = { ...legacyProfile, ...data };
  delete flattened.profile;

  // Handle stringified face descriptors if they exist
  let faceDescriptors = null;
  if (flattened.faceDescriptors) {
    try {
      faceDescriptors = typeof flattened.faceDescriptors === "string" 
        ? JSON.parse(flattened.faceDescriptors) 
        : flattened.faceDescriptors;
    } catch (e) {
      console.error("Error parsing face descriptors", e);
    }
  }

  const rawRoll = flattened.roll || studentId;
  const cleanRoll = typeof rawRoll === "string" ? rawRoll.replace("STAFF-", "") : rawRoll;

  return {
    ...flattened,
    id: studentId,
    role: flattened.role || (coll === "staff" ? "staff" : "student"),
    faceDescriptors,
    name: flattened.name || "Unknown",
    roll: cleanRoll
  };
}

export async function getAllStudents() {
  const [studentDocs, staffDocs] = await Promise.all([
    getDocs(collection(firestore, "students")),
    getDocs(collection(firestore, "staff"))
  ]);

  const mapDoc = (d, role) => {
    const data = d.data();
    
    // Flatten legacy 'profile' sub-object if it exists
    const legacyProfile = data.profile || {};
    const flattened = { ...legacyProfile, ...data };
    delete flattened.profile;

    let faceDescriptors = null;
    if (flattened.faceDescriptors) {
      try {
        faceDescriptors = typeof flattened.faceDescriptors === "string" 
          ? JSON.parse(flattened.faceDescriptors) 
          : flattened.faceDescriptors;
      } catch (e) {}
    }
    const rawRoll = flattened.roll || d.id;
    const cleanRoll = typeof rawRoll === "string" ? rawRoll.replace("STAFF-", "") : rawRoll;
    return { 
      ...flattened,
      id: d.id, 
      role: flattened.role || role, 
      name: flattened.name || "Unknown",
      roll: cleanRoll,
      status: flattened.status || "active",
      faceDescriptors 
    };
  };

  const students = studentDocs.docs.map(d => mapDoc(d, "student"));
  const staff    = staffDocs.docs.map(d => mapDoc(d, "staff"));

  return [...students, ...staff].sort((a, b) => (String(a.roll) || "").localeCompare(String(b.roll) || ""));
}

export async function updateStudentStatus(studentId, status) {
  const coll = getCollection(studentId);
  await setDoc(doc(firestore, coll, studentId), { status }, { merge: true });
}

export function subscribeAllStudents(callback) {
  const handler = async () => {
    const data = await getAllStudents();
    callback(data);
  };

  const unsub1 = onSnapshot(collection(firestore, "students"), handler);
  const unsub2 = onSnapshot(collection(firestore, "staff"), handler);

  return () => {
    unsub1();
    unsub2();
  };
}

export async function deleteStudent(studentId) {
  const coll = getCollection(studentId);
  await deleteDoc(doc(firestore, coll, studentId));
}

// ── FACE DESCRIPTORS (Firestore) ──────────────────

export async function saveFaceDescriptors(studentId, descriptors) {
  const coll = getCollection(studentId);
  // Firestore does NOT support nested arrays. We stringify the number[][]
  const serialized = JSON.stringify(descriptors.map(d => Array.from(d)));
  
  await setDoc(doc(firestore, coll, studentId), {
    faceDescriptors: serialized,
    faceEnrolled: true,
    enrolledAt: new Date().toISOString(),
  }, { merge: true });
}

export async function getEnrolledStudents() {
  const all = await getAllStudents();
  return all
    .filter(s => s.faceEnrolled && s.faceDescriptors)
    .map(s => ({
      ...s,
      faceDescriptors: s.faceDescriptors.map(arr => new Float32Array(arr)),
    }));
}

// ── ATTENDANCE (Firestore Sub-collections) ─────────

export async function markAttendance(studentId, status = "present") {
  const date = todayStr();
  const time = nowTime();
  const iso  = new Date().toISOString();
  const coll = getCollection(studentId);

  const studentRef = doc(firestore, coll, studentId);
  const profileSnap = await getDoc(studentRef);
  const profile = profileSnap.exists() ? profileSnap.data() : {};
  const role = profile.role || (coll === "staff" ? "staff" : "student");

  const attRef = doc(firestore, coll, studentId, "attendance", date);
  const attSnap = await getDoc(attRef);
  const data = attSnap.exists() ? attSnap.data() : null;

  let updatePayload = {};
  let isCheckOut = false;
  let alreadyDone = false;

  if (role === "staff") {
    const currentLogs = data?.logs || [];
    const lastLog = currentLogs[currentLogs.length - 1];
    const nextType = (!lastLog || lastLog.type === "check-out") ? "check-in" : "check-out";
    isCheckOut = nextType === "check-out";
    const newLog = { time, type: nextType, method: "face", markedAt: iso };
    updatePayload = { logs: [...currentLogs, newLog], status: "present", lastType: nextType, updatedAt: iso };
  } else {
    if (!data || !data.checkIn) {
      updatePayload = { checkIn: { time, method: "face", markedAt: iso }, status };
      // Update overall stats in profile doc
      const stats = profile.stats || { totalPresent: 0, totalDays: 0 };
      const computedDays = getWorkingDaysCount(profile.enrolledAt);
      const totalPresent = (stats.totalPresent || 0) + (status === "present" ? 1 : 0);
      // Ensure totalDays is at least the computed working days, but also handle legacy increments
      const totalDays = Math.max(computedDays, totalPresent, (stats.totalDays || 0) + 1);
      
      const rate = totalDays > 0 ? Math.round((totalPresent / totalDays) * 100) : 0;
      await setDoc(studentRef, { 
        stats: { totalPresent, totalDays, rate, lastSeen: date, lastStatus: status } 
      }, { merge: true });
    } else {
      // For students, we only want 1 attendance per day. 
      // Subsequent scans should just identify them for progress logging.
      alreadyDone = true;
      isCheckOut = false;
      return { isCheckOut, alreadyDone };
    }
  }

  // Save specific attendance record
  await setDoc(attRef, {
    ...updatePayload,
    time: role === "staff" ? time : (updatePayload.checkIn?.time || data?.checkIn?.time || time),
    method: "face",
    date
  }, { merge: true });

  // Update Global Index for Kiosk/Admin
  await setDoc(doc(firestore, "attendance_index", `${date}_${studentId}`), {
    status: role === "staff" ? "present" : (updatePayload.status || data?.status || status),
    time: role === "staff" ? time : (updatePayload.checkIn?.time || data?.checkIn?.time || time),
    role: role,
    name: profile.name || "Unknown",
    roll: profile.roll || studentId,
    studentId,
    date
  });

  return { isCheckOut, alreadyDone };
}

export async function getStudentAttendance(studentId) {
  const coll = getCollection(studentId);
  const snap = await getDocs(collection(firestore, coll, studentId, "attendance"));
  const data = {};
  snap.forEach(doc => { data[doc.id] = doc.data(); });
  return data;
}

export function subscribeTodayAttendance(callback, date = todayStr()) {
  const q = query(collection(firestore, "attendance_index"), where("date", "==", date));
  return onSnapshot(q, snap => {
    const data = {};
    snap.forEach(d => {
      const rec = d.data();
      data[rec.studentId] = rec;
    });
    callback(data);
  });
}

// ── SKILLS ──────────────────────────────────────

export async function saveSkillCategory(category) {
  const id = category.id || doc(collection(firestore, "settings", "skills", "categories")).id;
  await setDoc(doc(firestore, "settings", "skills", "categories", id), { ...category, id });
  return id;
}

export function subscribeSkillCategories(callback) {
  return onSnapshot(collection(firestore, "settings", "skills", "categories"), snap => {
    const list = snap.docs.map(d => d.data());
    callback(list);
  });
}

export async function saveUserSkill(studentId, skill) {
  const coll = getCollection(studentId);
  const id = skill.id || doc(collection(firestore, coll, studentId, "skills")).id;
  await setDoc(doc(firestore, coll, studentId, "skills", id), { ...skill, id, studentId });
  return id;
}

export async function deleteUserSkill(studentId, skillId) {
  const coll = getCollection(studentId);
  await deleteDoc(doc(firestore, coll, studentId, "skills", skillId));
}

export function subscribeUserSkills(studentId, callback) {
  const coll = getCollection(studentId);
  const q = query(collection(firestore, coll, studentId, "skills"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

// ── CRM ──────────────────────────────────────────

export function getCRMCollectionRef(studentId) {
  if (studentId) {
    const coll = getCollection(studentId);
    return collection(firestore, coll, studentId, "crm_records");
  }
  return collection(firestore, "crm_records");
}

export function getCRMRecordRef(studentId, prospectId) {
  if (studentId) {
    const coll = getCollection(studentId);
    return doc(firestore, coll, studentId, "crm_records", prospectId);
  }
  return doc(firestore, "crm_records", prospectId);
}

export function parseCRMDate(val) {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (typeof val.seconds === "number") return new Date(val.seconds * 1000);
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Dynamically derives:
 * - `last_meeting_date` ('L'): Most recent interaction `date` where `date <= current date/time`
 * - `next_meeting_date` ('N'): Most recent or upcoming `next_schedule_date` logged in interactions
 */
export function computeScheduleSummaryFromInteractions(interactions = []) {
  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);

  let latestPastOrPresentDate = null;
  const upcomingSchedules = [];
  const allSchedulesByRecency = [];

  // Sort interactions by interaction date desc, then created_at desc
  const sorted = [...interactions].sort((a, b) => {
    const dateA = parseCRMDate(a.date)?.getTime() || 0;
    const dateB = parseCRMDate(b.date)?.getTime() || 0;
    if (dateB !== dateA) return dateB - dateA;
    const createdA = parseCRMDate(a.created_at)?.getTime() || 0;
    const createdB = parseCRMDate(b.created_at)?.getTime() || 0;
    return createdB - createdA;
  });

  for (const item of sorted) {
    const itemDate = parseCRMDate(item.date);
    if (itemDate && itemDate.getTime() <= endOfToday.getTime()) {
      if (!latestPastOrPresentDate || itemDate.getTime() > latestPastOrPresentDate.getTime()) {
        latestPastOrPresentDate = itemDate;
      }
    }

    const nextDate = parseCRMDate(item.next_schedule_date);
    if (nextDate) {
      allSchedulesByRecency.push(nextDate);
      if (nextDate.getTime() >= startOfToday.getTime()) {
        upcomingSchedules.push(nextDate);
      }
    }
  }

  // Pick the most recent interaction's upcoming follow-up, or latest logged follow-up
  let resolvedNextDate = null;
  if (allSchedulesByRecency.length > 0) {
    // Most recent interaction's scheduled date takes priority if it is upcoming,
    // otherwise pick the nearest upcoming schedule date, or fallback to most recently logged schedule date
    const mostRecentLoggedSchedule = allSchedulesByRecency[0];
    if (mostRecentLoggedSchedule.getTime() >= startOfToday.getTime()) {
      resolvedNextDate = mostRecentLoggedSchedule;
    } else if (upcomingSchedules.length > 0) {
      upcomingSchedules.sort((a, b) => a.getTime() - b.getTime());
      resolvedNextDate = upcomingSchedules[0];
    } else {
      resolvedNextDate = mostRecentLoggedSchedule;
    }
  }

  return {
    last_meeting_date: latestPastOrPresentDate ? Timestamp.fromDate(latestPastOrPresentDate) : null,
    next_meeting_date: resolvedNextDate ? Timestamp.fromDate(resolvedNextDate) : null,
    lastMeetingDate: latestPastOrPresentDate ? latestPastOrPresentDate.toISOString().split("T")[0] : "",
    nextMeetingDate: resolvedNextDate ? resolvedNextDate.toISOString().split("T")[0] : "",
  };
}

export async function saveCRMRecord(studentId, record) {
  const collRef = getCRMCollectionRef(studentId);
  const isNew = !record.id;
  const id = record.id || doc(collRef).id;

  const payload = {
    ...record,
    id,
    ...(studentId ? { studentId } : {}),
    updatedAt: new Date().toISOString(),
  };

  // For newly created prospects, initialize summary schedule fields to null/empty
  // since dates are managed dynamically via the interactions subcollection.
  if (isNew) {
    payload.last_meeting_date = null;
    payload.next_meeting_date = null;
    payload.lastMeetingDate = "";
    payload.nextMeetingDate = "";
  }

  await setDoc(doc(collRef, id), payload, { merge: true });
  return id;
}

export async function deleteCRMRecord(studentId, recordId) {
  await deleteDoc(getCRMRecordRef(studentId, recordId));
}

export function subscribeCRMRecords(studentId, callback) {
  const q = query(getCRMCollectionRef(studentId));
  return onSnapshot(q, async snap => {
    const baseDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Emit immediately for fast initial render
    callback(baseDocs);

    // Dynamically derive 'L' (last_meeting_date) and 'N' (next_meeting_date) from each prospect's interactions subcollection
    try {
      const hydrated = await Promise.all(
        baseDocs.map(async record => {
          const prospectRef = getCRMRecordRef(studentId, record.id);
          const intSnap = await getDocs(collection(prospectRef, "interactions"));
          const interactions = intSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
          const summary = computeScheduleSummaryFromInteractions(interactions);
          return {
            ...record,
            ...summary,
          };
        })
      );
      callback(hydrated);
    } catch (err) {
      console.error("Failed to hydrate CRM interaction summaries:", err);
    }
  });
}

/**
 * Adds or updates an interaction under `{prospectPath}/interactions/{interactionId}`
 * and atomically recalculates and updates the parent prospect's `last_meeting_date` & `next_meeting_date`.
 */
export async function saveCRMInteraction(studentId, prospectId, interactionInput) {
  const prospectRef = getCRMRecordRef(studentId, prospectId);
  const interactionsColRef = collection(prospectRef, "interactions");
  const isUpdate = Boolean(interactionInput.id);
  const interactionRef = isUpdate
    ? doc(interactionsColRef, interactionInput.id)
    : doc(interactionsColRef);

  const interactionDateObj = interactionInput.date
    ? new Date(interactionInput.date)
    : new Date();
  const interactionTimestamp = Timestamp.fromDate(interactionDateObj);

  const nextScheduleDateObj = interactionInput.next_schedule_date
    ? new Date(interactionInput.next_schedule_date)
    : null;
  const nextScheduleTimestamp = nextScheduleDateObj
    ? Timestamp.fromDate(nextScheduleDateObj)
    : null;

  const interactionDoc = {
    id: interactionRef.id,
    type: interactionInput.type === "Call" ? "Call" : "Meeting",
    date: interactionTimestamp,
    duration: interactionInput.duration !== "" && interactionInput.duration !== undefined
      ? Number(interactionInput.duration) || interactionInput.duration
      : 0,
    subject: (interactionInput.subject || "").trim(),
    next_schedule_date: nextScheduleTimestamp,
    ...(isUpdate
      ? { updated_at: serverTimestamp() }
      : { created_at: serverTimestamp() }),
  };

  // Read existing interactions so 'L' and 'N' accurately reflect all interactions
  const existingSnap = await getDocs(interactionsColRef);
  const existingList = existingSnap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(item => item.id !== interactionRef.id);

  const optimisticInteraction = {
    ...interactionDoc,
    created_at: interactionInput.created_at || Timestamp.now(),
  };

  const allInteractions = [optimisticInteraction, ...existingList];
  const scheduleSummary = computeScheduleSummaryFromInteractions(allInteractions);

  const parentUpdate = {
    ...scheduleSummary,
    updatedAt: new Date().toISOString(),
  };

  const batch = writeBatch(firestore);
  batch.set(interactionRef, interactionDoc, { merge: true });
  batch.set(prospectRef, parentUpdate, { merge: true });
  await batch.commit();

  return {
    interaction: optimisticInteraction,
    parentUpdate,
  };
}

export async function addCRMInteraction(studentId, prospectId, interactionInput) {
  return saveCRMInteraction(studentId, prospectId, interactionInput);
}

export async function getCRMInteractions(studentId, prospectId) {
  const prospectRef = getCRMRecordRef(studentId, prospectId);
  const q = query(collection(prospectRef, "interactions"), orderBy("date", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export function subscribeCRMInteractions(studentId, prospectId, callback) {
  const prospectRef = getCRMRecordRef(studentId, prospectId);
  const q = query(collection(prospectRef, "interactions"), orderBy("date", "desc"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ── DIARY (Firestore Sub-collections) ─────────────

export async function saveDiaryEntry(studentId, entry, date = todayStr()) {
  const coll = getCollection(studentId);
  await setDoc(doc(firestore, coll, studentId, "diary", date), {
    topics:  entry.topics  || "",
    notes:   entry.notes   || "",
    date,
    savedAt: new Date().toISOString(),
  });
}

export async function getStudentDiary(studentId) {
  const coll = getCollection(studentId);
  const snap = await getDocs(query(collection(firestore, coll, studentId, "diary"), orderBy("date", "desc")));
  return snap.docs.map(d => d.data());
}

export function subscribeDiary(studentId, callback) {
  const coll = getCollection(studentId);
  const q = query(collection(firestore, coll, studentId, "diary"), orderBy("date", "desc"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

// ── SETTINGS (Firestore) ─────────────────────────

export const DEFAULT_CATEGORIES = [
  { id: "theory",     label: "Theory Study",        color: "#3D7EFF" },
  { id: "practical",  label: "Practical / Lab Work", color: "#16A349" },
  { id: "assignment", label: "Assignment / Project",  color: "#7C3AED" },
  { id: "revision",   label: "Revision / Practice",  color: "#D97706" },
];

export const CATEGORIES = DEFAULT_CATEGORIES;

let cachedCategories = DEFAULT_CATEGORIES;

export function subscribeCategories(callback) {
  return onSnapshot(collection(firestore, "settings", "categories", "list"), snap => {
    const list = snap.docs.length > 0 ? snap.docs.map(d => d.data()) : DEFAULT_CATEGORIES;
    cachedCategories = list;
    callback(list);
  });
}

export async function saveCategory(category) {
  const id = category.id || doc(collection(firestore, "settings", "categories", "list")).id;
  await setDoc(doc(firestore, "settings", "categories", "list", id), { ...category, id });
  return id;
}

export async function deleteCategory(id) {
  await deleteDoc(doc(firestore, "settings", "categories", "list", id));
}

export function subscribeTopics(callback) {
  return onSnapshot(collection(firestore, "settings", "topics", "list"), snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

export async function saveTopic(topic) {
  const id = topic.id || doc(collection(firestore, "settings", "topics", "list")).id;
  await setDoc(doc(firestore, "settings", "topics", "list", id), { ...topic, id });
  return id;
}

export async function deleteTopic(id) {
  await deleteDoc(doc(firestore, "settings", "topics", "list", id));
}

export async function getNextRollNumber() {
  const snap = await getDoc(doc(firestore, "settings", "counters"));
  return (snap.exists() ? (snap.data().lastRollNumber || 0) : 0) + 1;
}

export async function incrementRollNumber() {
  const ref = doc(firestore, "settings", "counters");
  const snap = await getDoc(ref);
  const next = (snap.exists() ? (snap.data().lastRollNumber || 0) : 0) + 1;
  await setDoc(ref, { lastRollNumber: next }, { merge: true });
  return next;
}

// ── TARGETS ───────────────────────────────────────

export async function setStudentTarget(studentId, categoryId, targetHHMM) {
  const coll = getCollection(studentId);
  const targetMinutes = HHMMToMinutes(targetHHMM);
  await setDoc(doc(firestore, coll, studentId, "progress_targets", categoryId), {
    targetMinutes,
    categoryId,
    label: cachedCategories.find(c => c.id === categoryId)?.label || categoryId,
    setAt: new Date().toISOString(),
  });
}

export async function setBulkTargets(studentIds, targets) {
  for (const studentId of studentIds) {
    for (const [categoryId, hhmm] of Object.entries(targets)) {
      await setStudentTarget(studentId, categoryId, hhmm);
    }
  }
}

export async function getStudentTargets(studentId) {
  const coll = getCollection(studentId);
  const snap = await getDocs(collection(firestore, coll, studentId, "progress_targets"));
  const data = {};
  snap.forEach(d => { data[d.id] = d.data(); });
  return data;
}

// ── TASK TARGETS ───────────────────────────────────

export async function setStudentTaskTarget(studentId, topicName, targetHHMM) {
  const coll = getCollection(studentId);
  const targetMinutes = HHMMToMinutes(targetHHMM);
  // Using topicName as ID (sanitized)
  const taskId = topicName.toLowerCase().replace(/[^a-z0-t0-9]/g, "_");
  await setDoc(doc(firestore, coll, studentId, "task_targets", taskId), {
    targetMinutes,
    topicName,
    setAt: new Date().toISOString(),
  });
}

export async function getStudentTaskTargets(studentId) {
  const coll = getCollection(studentId);
  const snap = await getDocs(collection(firestore, coll, studentId, "task_targets"));
  const data = {};
  snap.forEach(d => { data[d.data().topicName] = d.data(); });
  return data;
}

// ── LOG ENTRIES ───────────────────────────────────

export async function saveProgressEntry(studentId, entry) {
  const coll = getCollection(studentId);
  const loggedMinutes = HHMMToMinutes(entry.loggedHHMM);
  const targetMinutes = HHMMToMinutes(entry.targetHHMM);
  const date = entry.date || todayStr();
  
  // Flattened Structure: students/{studentId}/progress_logs/{entryId}
  const logRef = collection(firestore, coll, studentId, "progress_logs");
  const entryId = entry.entryId || doc(logRef).id;
  const entryRef = doc(logRef, entryId);

  // Activity Burn Calculation (MET based)
  let kcalBurned = 0;
  try {
    const student = await getStudent(studentId);
    if (student?.weight) {
      const weight = parseFloat(student.weight);
      const metMap = { 
        "Walking": 3.8, 
        "Running": 8.0, 
        "Cycling": 6.0,
        "Yoga": 2.5, 
        "Gym": 6.0,
        "Workout": 6.0,
        "Sex": 5.8,
        "Exercise": 5.0,
        "Fitness": 5.0, 
        "Sports": 6.0, 
        "Swimming": 7.0,
        "Study": 1.3, 
        "Work": 1.5, 
        "Computer": 1.5, 
        "Duty": 1.5,
        "Sleep": 1.0,
        "Rest": 1.0
      };
      // Try to find MET by category label or topic name
      const category = (entry.category || "").toLowerCase();
      const topic = (entry.topicName || "").toLowerCase();
      let met = 1.3; // Default (Sitting quietly)
      
      // Use word boundaries for matching to avoid partial matches (like 'e' in 'sleep')
      for (const [key, value] of Object.entries(metMap)) {
        const k = key.toLowerCase();
        const regex = new RegExp("\\b" + k + "\\b", "i");
        if (regex.test(category) || regex.test(topic)) {
          met = value;
          break; // Use the first match
        }
      }
      // Calculate NET calories burned (MET - 1) to avoid double counting BMR 
      // when this is added to the 24h BMR total in reports.
      kcalBurned = Math.round(Math.max(0, met - 1) * weight * (loggedMinutes / 60));
    }
  } catch (e) { console.error("Kcal calc error", e); }

  // If editing, get previous logged minutes to adjust stats
  let diffLogged = loggedMinutes;
  const prevSnap = entry.entryId ? await getDoc(entryRef) : null;
  if (prevSnap?.exists()) {
    diffLogged = loggedMinutes - (prevSnap.data().loggedMinutes || 0);
  }

  const record = {
    entryId,
    studentId,
    category:      entry.category,
    topicName:     entry.topicName   || "",
    loggedMinutes,
    targetMinutes,
    startTime:     entry.startTime   || "",
    endTime:       entry.endTime     || "",
    notes:         entry.notes       || "",
    kcalBurned:    kcalBurned,
    date,
    savedAt:       entry.entryId ? (prevSnap?.data()?.savedAt || new Date().toISOString()) : new Date().toISOString(),
    updatedAt:     new Date().toISOString(),
  };

  await setDoc(entryRef, record);

  // Update Stats
  const statRef = doc(firestore, coll, studentId, "progress_stats", entry.category);
  const statSnap = await getDoc(statRef);
  const existing = statSnap.exists() ? statSnap.data() : { totalLoggedMinutes: 0, totalTargetMinutes: 0, entryCount: 0 };

  const targetSnap = await getDoc(doc(firestore, coll, studentId, "progress_targets", entry.category));
  const adminTarget = targetSnap.exists() ? targetSnap.data().targetMinutes : 0;

  const newLogged = (existing.totalLoggedMinutes || 0) + diffLogged;
  const newTarget = (existing.totalTargetMinutes || 0) + (entry.entryId ? 0 : targetMinutes);
  const newCount  = (existing.entryCount || 0) + (entry.entryId ? 0 : 1);
  const rate      = adminTarget > 0 ? Math.min(100, Math.round((newLogged / adminTarget) * 100)) : 0;

  await setDoc(statRef, {
    totalLoggedMinutes: newLogged,
    totalTargetMinutes: newTarget,
    entryCount:         newCount,
    rate,
    lastUpdated:        date,
  }, { merge: true });

  return entryId;
}

export async function deleteProgressEntry(studentId, entryId, category, loggedMinutes, date) {
  const coll = getCollection(studentId);
  await deleteDoc(doc(firestore, coll, studentId, "progress_logs", entryId));
  
  const statRef = doc(firestore, coll, studentId, "progress_stats", category);
  const statSnap = await getDoc(statRef);
  if (statSnap.exists()) {
    const s = statSnap.data();
    const newLogged = Math.max(0, (s.totalLoggedMinutes || 0) - loggedMinutes);
    await updateDoc(statRef, {
      totalLoggedMinutes: newLogged,
      entryCount: Math.max(0, (s.entryCount || 1) - 1)
    });
  }
}

export async function getStudentProgressLogs(studentId) {
  const coll = getCollection(studentId);
  const q = query(
    collection(firestore, coll, studentId, "progress_logs"),
    orderBy("savedAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data());
}

export function subscribeProgressLogs(studentId, callback) {
  const coll = getCollection(studentId);
  const q = query(
    collection(firestore, coll, studentId, "progress_logs"),
    orderBy("savedAt", "desc")
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

export async function getFilteredLogs({ studentId, date, limitCount = 100 }) {
  let q;

  try {
    if (studentId) {
      // ── DIRECT PATH (100% INDEX-FREE & CHEAPEST) ──
      const coll = getCollection(studentId);
      const logRef = collection(firestore, coll, studentId, "progress_logs");
      
      if (date) {
        // Simple equality filter on sub-collection never requires a custom index
        q = query(logRef, where("date", "==", date), limit(limitCount));
      } else {
        q = query(logRef, limit(limitCount));
      }
    } else {
      // ── COLLECTION GROUP (Requires Index) ──
      // If no student is selected, we only allow pulling if a date is set
      if (!date) return [];
      
      q = query(collectionGroup(firestore, "progress_logs"), where("date", "==", date), limit(limitCount));
    }

    const snap = await getDocs(q);
    let allLogs = snap.docs.map(d => d.data());
    
    // Sort locally (Latest first)
    allLogs.sort((a, b) => {
      const dateCompare = (b.date || "").localeCompare(a.date || "");
      if (dateCompare !== 0) return dateCompare;
      return (b.savedAt || "").localeCompare(a.savedAt || "");
    });

    return allLogs;

  } catch (err) {
    if (err.message.includes("requires an index")) {
      console.error("Firestore Index Error: Please select a specific Member to avoid this.");
    }
    throw err;
  }
}

export async function getStudentProgressStats(studentId) {
  const coll = getCollection(studentId);
  const snap = await getDocs(collection(firestore, coll, studentId, "progress_stats"));
  const data = {};
  snap.forEach(d => { data[d.id] = d.data(); });
  return data;
}

// ── DIET & NUTRITION ─────────────────────────────

export async function getAllDietItems() {
  const snap = await getDocs(query(collection(firestore, "diet_items"), orderBy("name", "asc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function addDietItem(item) {
  const ref = doc(collection(firestore, "diet_items"));
  await setDoc(ref, { ...item, createdAt: new Date().toISOString() });
  return ref.id;
}

export async function saveDietEntry(studentId, entry) {
  const coll = getCollection(studentId);
  const date = entry.date || todayStr();
  const logRef = collection(firestore, coll, studentId, "diet_logs");
  const entryId = entry.entryId || doc(logRef).id;
  
  const record = {
    ...entry,
    entryId,
    studentId,
    date,
    savedAt: new Date().toISOString(),
  };

  await setDoc(doc(logRef, entryId), record);
  return entryId;
}

export function subscribeDietLogs(studentId, callback, limitCount = 1000) {
  const coll = getCollection(studentId);
  const q = query(
    collection(firestore, coll, studentId, "diet_logs"),
    orderBy("savedAt", "desc"),
    limit(limitCount)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

export async function deleteDietEntry(studentId, entryId) {
  const coll = getCollection(studentId);
  await deleteDoc(doc(firestore, coll, studentId, "diet_logs", entryId));
}

export async function getFilteredDietLogs({ studentId, date }) {
  let q;
  if (studentId) {
    const coll = getCollection(studentId);
    const ref = collection(firestore, coll, studentId, "diet_logs");
    q = date ? query(ref, where("date", "==", date)) : query(ref);
  } else {
    // Collection Group search for all students on a specific date
    q = query(collectionGroup(firestore, "diet_logs"), where("date", "==", date));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data());
}

// ── MESSAGE LOGS (Firestore) ──────────────────────

export async function saveMessageLog(log) {
  const logRef = collection(firestore, "message_logs");
  const id = doc(logRef).id;
  await setDoc(doc(logRef, id), {
    id,
    to: log.to || "",
    body: log.body || "",
    status: log.status || "pending",
    error: log.error || null,
    studentId: log.studentId || "",
    studentName: log.studentName || "",
    type: log.type || "attendance",
    timestamp: new Date().toISOString(),
  });
}

export function subscribeMessageLogs(callback, limitCount = 50) {
  const q = query(
    collection(firestore, "message_logs"),
    orderBy("timestamp", "desc"),
    limit(limitCount)
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => d.data()));
  });
}

export function subscribeProgressStats(studentId, callback) {
  const coll = getCollection(studentId);
  return onSnapshot(collection(firestore, coll, studentId, "progress_stats"), snap => {
    const data = {};
    snap.forEach(d => { data[d.id] = d.data(); });
    callback(data);
  });
}

export async function getAllProgressReport() {
  const students = await getDocs(collection(firestore, "students"));
  const staff = await getDocs(collection(firestore, "staff"));
  
  const all = [...students.docs, ...staff.docs];
  const report = [];

  for (const userDoc of all) {
    const id = userDoc.id;
    const profile = userDoc.data();
    if (profile.status === "inactive") continue;
    
    const targetsSnap = await getDocs(collection(firestore, getCollection(id), id, "progress_targets"));
    const statsSnap = await getDocs(collection(firestore, getCollection(id), id, "progress_stats"));
    
    const targets = {}; targetsSnap.forEach(d => targets[d.id] = d.data());
    const stats = {}; statsSnap.forEach(d => stats[d.id] = d.data());

    const categoryData = cachedCategories.map(cat => {
      const s = stats[cat.id]   || {};
      const t = targets[cat.id] || {};
      return {
        ...cat,
        adminTargetMinutes:  t.targetMinutes || 0,
        totalLoggedMinutes:  s.totalLoggedMinutes || 0,
        entryCount:          s.entryCount || 0,
        rate:                s.rate || 0,
      };
    });

    const totalLogged = categoryData.reduce((a, c) => a + c.totalLoggedMinutes, 0);
    const totalTarget = categoryData.reduce((a, c) => a + c.adminTargetMinutes, 0);
    const overallRate = totalTarget > 0 ? Math.min(100, Math.round((totalLogged / totalTarget) * 100)) : 0;

    report.push({
      id,
      name: profile.name || "Unknown",
      roll: profile.roll || "",
      batch: profile.batch || "",
      categoryData,
      totalLogged,
      totalTarget,
      overallRate
    });
  }

  return report.sort((a, b) => (String(a.roll) || "").localeCompare(String(b.roll) || ""));
}

// ── REPORTS ───────────────────────────────────────

export async function getFullReportData() {
  const all = await getAllStudents();
  
  // Fetch attendance index for the last 7 working days to show dots
  const recentDates = getLastNWorkingDays(7);
  const q = query(collection(firestore, "attendance_index"), where("date", ">=", recentDates[0]));
  const snap = await getDocs(q);
  
  const recentAtt = {}; // { studentId: { date: status } }
  snap.forEach(d => {
    const r = d.data();
    if (!recentAtt[r.studentId]) recentAtt[r.studentId] = {};
    recentAtt[r.studentId][r.date] = r.status;
  });

  return all.filter(s => s.status !== "inactive").map(s => {
    const stats = s.stats || {};
    // Calculate total days since enrollment, excluding Sundays
    const computedDays = getWorkingDaysCount(s.enrolledAt);
    const totalPresent = stats.totalPresent || 0;
    
    // totalDays is the higher of computedDays, totalPresent, or legacy stats
    const totalDays = Math.max(computedDays, totalPresent, stats.totalDays || 0);
    
    const rate = totalDays > 0 ? Math.round((totalPresent / totalDays) * 100) : 0;

    // Build recentDays dots for the table (last 7 working days)
    const recentDays = recentDates.map(date => ({
      date,
      status: recentAtt[s.id]?.[date] || "absent"
    }));

    return {
      ...s,
      totalPresent,
      totalDays,
      rate,
      lastSeen:     stats.lastSeen     || null,
      lastStatus:   stats.lastStatus   || null,
      diaryCount:   stats.diaryCount   || 0,
      recentDays
    };
  });
}

export async function getAttendanceRangeData(startDate, endDate) {
  const q = query(
    collection(firestore, "attendance_index"), 
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  );
  const snap = await getDocs(q);
  const data = {}; // { date: { studentId: status } }
  snap.forEach(doc => {
    const r = doc.data();
    if (!data[r.date]) data[r.date] = {};
    data[r.date][r.studentId] = r.status;
  });
  return data;
}

export async function syncAllStudentStats() {
  const students = await getAllStudents();
  const allAttSnap = await getDocs(collection(firestore, "attendance_index"));
  
  const attMap = {}; // { studentId: count }
  allAttSnap.forEach(d => {
    const r = d.data();
    if (r.status === "present") {
      attMap[r.studentId] = (attMap[r.studentId] || 0) + 1;
    }
  });

  const promises = students.map(async (s) => {
    const totalPresent = attMap[s.id] || 0;
    const computedDays = getWorkingDaysCount(s.enrolledAt);
    const totalDays = Math.max(computedDays, totalPresent, s.stats?.totalDays || 0);
    const rate = totalDays > 0 ? Math.round((totalPresent / totalDays) * 100) : 0;
    
    const coll = getCollection(s.id);
    await setDoc(doc(firestore, coll, s.id), {
      stats: { ...s.stats, totalPresent, totalDays, rate }
    }, { merge: true });
  });

  await Promise.all(promises);
}

// ── LMS: COURSES ───────────────────────────────────

export async function createCourse(courseData) {
  const courseRef = doc(collection(firestore, "courses"));
  const courseId = courseRef.id;
  await setDoc(courseRef, {
    courseId,
    name: courseData.name,
    totalLessons: parseInt(courseData.totalLessons) || 0,
    totalDurationHours: parseFloat(courseData.totalDurationHours) || 0,
    classHours: parseFloat(courseData.classHours) || 0,
    practiceHours: parseFloat(courseData.practiceHours) || 0,
    fees: parseFloat(courseData.fees) || 0,
    createdAt: new Date().toISOString(),
  });
  return courseId;
}

export async function getAllCourses() {
  const snap = await getDocs(query(collection(firestore, "courses"), orderBy("name", "asc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getCourse(courseId) {
  const snap = await getDoc(doc(firestore, "courses", courseId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateCourse(courseId, courseData) {
  await setDoc(doc(firestore, "courses", courseId), {
    ...courseData,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

export async function deleteCourse(courseId) {
  // Delete all lessons first
  const lessonsSnap = await getDocs(collection(firestore, "courses", courseId, "lessons"));
  const deletePromises = lessonsSnap.docs.map(d => deleteDoc(doc(firestore, "courses", courseId, "lessons", d.id)));
  await Promise.all(deletePromises);
  // Delete course
  await deleteDoc(doc(firestore, "courses", courseId));
}

// ── LMS: LESSONS ───────────────────────────────────

export async function addLesson(courseId, lessonData) {
  const lessonRef = doc(collection(firestore, "courses", courseId, "lessons"));
  const lessonId = lessonRef.id;
  // Get current lesson count for order
  const snap = await getDocs(collection(firestore, "courses", courseId, "lessons"));
  const order = lessonData.order || (snap.size + 1);
  
  await setDoc(lessonRef, {
    lessonId,
    title: lessonData.title,
    content: lessonData.content || "",
    category: lessonData.category || "class", // "class" or "practice"
    order,
    createdAt: new Date().toISOString(),
  });
  return lessonId;
}

export async function getLessons(courseId) {
  const snap = await getDocs(query(collection(firestore, "courses", courseId, "lessons"), orderBy("order", "asc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateLesson(courseId, lessonId, lessonData) {
  await setDoc(doc(firestore, "courses", courseId, "lessons", lessonId), {
    ...lessonData,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

export async function deleteLesson(courseId, lessonId) {
  await deleteDoc(doc(firestore, "courses", courseId, "lessons", lessonId));
}

// ── LMS: ENROLLMENTS ───────────────────────────────

export async function enrollStudent(studentId, courseId, courseName, feePaid = false) {
  const enrollmentRef = doc(firestore, "students", studentId, "enrollments", courseId);
  await setDoc(enrollmentRef, {
    courseId,
    courseName,
    enrolledAt: new Date().toISOString(),
    feePaid,
    currentLessonIndex: 0,
    completedLessons: [],
    updatedAt: new Date().toISOString(),
  });
}

export async function getStudentEnrollments(studentId) {
  const snap = await getDocs(collection(firestore, "students", studentId, "enrollments"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getStudentEnrollment(studentId, courseId) {
  const snap = await getDoc(doc(firestore, "students", studentId, "enrollments", courseId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateEnrollment(studentId, courseId, data) {
  await setDoc(doc(firestore, "students", studentId, "enrollments", courseId), {
    ...data,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

export async function updateFeeStatus(studentId, courseId, feePaid) {
  await setDoc(doc(firestore, "students", studentId, "enrollments", courseId), {
    feePaid,
    updatedAt: new Date().toISOString(),
  }, { merge: true });
}

// ── LMS: LESSON COMPLETION ─────────────────────────

export async function markLessonComplete(studentId, courseId, lessonId, staffId, staffName) {
  const enrollment = await getStudentEnrollment(studentId, courseId);
  if (!enrollment) throw new Error("Enrollment not found");

  const completedLessons = enrollment.completedLessons || [];
  // Check if already completed
  if (completedLessons.some(l => l.lessonId === lessonId)) {
    return; // Already completed
  }

  completedLessons.push({
    lessonId,
    completedAt: new Date().toISOString(),
    completedByStaffId: staffId,
    completedByStaffName: staffName,
  });

  // Find current lesson index
  const lessons = await getLessons(courseId);
  const completedIds = completedLessons.map(l => l.lessonId);
  const nextIndex = lessons.findIndex(l => !completedIds.includes(l.id));
  
  await updateEnrollment(studentId, courseId, {
    completedLessons,
    currentLessonIndex: nextIndex >= 0 ? nextIndex : lessons.length,
  });
}

export async function unmarkLessonComplete(studentId, courseId, lessonId) {
  const enrollment = await getStudentEnrollment(studentId, courseId);
  if (!enrollment) throw new Error("Enrollment not found");

  const completedLessons = (enrollment.completedLessons || []).filter(l => l.lessonId !== lessonId);
  
  // Find new current lesson index (first incomplete lesson)
  const lessons = await getLessons(courseId);
  const completedIds = completedLessons.map(l => l.lessonId);
  const newIndex = lessons.findIndex(l => !completedIds.includes(l.id));
  
  await updateEnrollment(studentId, courseId, {
    completedLessons,
    currentLessonIndex: newIndex >= 0 ? newIndex : 0,
  });
}

export async function getStudentCourseProgress(studentId) {
  const enrollments = await getStudentEnrollments(studentId);
  const progress = [];
  
  for (const enrollment of enrollments) {
    const course = await getCourse(enrollment.courseId);
    const lessons = await getLessons(enrollment.courseId);
    progress.push({
      ...enrollment,
      course,
      lessons,
    });
  }
  return progress;
}

// ═══════════════════════════════════════════════════════════════
// ACCOUNTING MODULE
// ═══════════════════════════════════════════════════════════════

// ── CUSTOMERS ──────────────────────────────────────────────────
export async function addCustomer(data) {
  const docRef = doc(collection(firestore, "accounting_customers"));
  await setDoc(docRef, {
    ...data,
    createdAt: new Date().toISOString(),
  });
  return docRef.id;
}

export async function updateCustomer(customerId, data) {
  const docRef = doc(firestore, "accounting_customers", customerId);
  await updateDoc(docRef, { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteCustomer(customerId) {
  await deleteDoc(doc(firestore, "accounting_customers", customerId));
}

export async function getCustomer(customerId) {
  const docSnap = await getDoc(doc(firestore, "accounting_customers", customerId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function getAllCustomers() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_customers"), orderBy("name", "asc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function searchCustomers(queryText) {
  const snapshot = await getDocs(query(
    collection(firestore, "accounting_customers"),
    where("name", ">=", queryText),
    where("name", "<=", queryText + "\uf8ff"),
    orderBy("name", "asc")
  ));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── CHART OF ACCOUNTS ──────────────────────────────────────────
const ACCOUNT_NATURES = [
  "Current Liability", "Long Term Liability", "Fixed Assets", 
  "Current Assets", "Cash", "Bank", "Creditor", "Debitor", 
  "Capital", "Income", "Expense"
];

export { ACCOUNT_NATURES };

export async function addAccount(data) {
  const docRef = doc(collection(firestore, "accounting_accounts"));
  await setDoc(docRef, {
    ...data,
    createdAt: new Date().toISOString(),
  });
  return docRef.id;
}

export async function updateAccount(accountId, data) {
  const docRef = doc(firestore, "accounting_accounts", accountId);
  await updateDoc(docRef, { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteAccount(accountId) {
  await deleteDoc(doc(firestore, "accounting_accounts", accountId));
}

export async function getAccount(accountId) {
  const docSnap = await getDoc(doc(firestore, "accounting_accounts", accountId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function getAllAccounts() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_accounts"), orderBy("name", "asc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getAccountsByNature(nature) {
  const snapshot = await getDocs(query(
    collection(firestore, "accounting_accounts"),
    where("nature", "==", nature),
    orderBy("name", "asc")
  ));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── INVOICE ITEMS ───────────────────────────────────────────────
export async function addInvoiceItem(data) {
  const docRef = doc(collection(firestore, "accounting_items"));
  await setDoc(docRef, { ...data, createdAt: new Date().toISOString() });
  return docRef.id;
}

export async function updateInvoiceItem(itemId, data) {
  await updateDoc(doc(firestore, "accounting_items", itemId), data);
}

export async function deleteInvoiceItem(itemId) {
  await deleteDoc(doc(firestore, "accounting_items", itemId));
}

export async function getAllInvoiceItems() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_items"), orderBy("name", "asc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── INVOICES ───────────────────────────────────────────────────
export async function getNextInvoiceNumber() {
  const settingDoc = await getDoc(doc(firestore, "accounting_settings", "invoice_counter"));
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  return `INV-${String(current).padStart(5, "0")}`;
}

export async function incrementInvoiceNumber() {
  const docRef = doc(firestore, "accounting_settings", "invoice_counter");
  const settingDoc = await getDoc(docRef);
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  await setDoc(docRef, { nextNumber: current + 1 }, { merge: true });
}

export async function addInvoice(data) {
  const invoiceNo = await getNextInvoiceNumber();
  const docRef = doc(collection(firestore, "accounting_invoices"));
  await setDoc(docRef, {
    ...data,
    invoiceNo,
    createdAt: new Date().toISOString(),
  });
  await incrementInvoiceNumber();
  return { id: docRef.id, invoiceNo };
}

export async function updateInvoice(invoiceId, data) {
  const docRef = doc(firestore, "accounting_invoices", invoiceId);
  await updateDoc(docRef, { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteInvoice(invoiceId) {
  await deleteDoc(doc(firestore, "accounting_invoices", invoiceId));
}

export async function getInvoice(invoiceId) {
  const docSnap = await getDoc(doc(firestore, "accounting_invoices", invoiceId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function getAllInvoices() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_invoices"), orderBy("date", "desc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getInvoicesByStatus(status) {
  const snapshot = await getDocs(query(
    collection(firestore, "accounting_invoices"),
    where("status", "==", status),
    orderBy("date", "desc")
  ));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── EXPENSES ───────────────────────────────────────────────────
export async function getNextVoucherNumber() {
  const settingDoc = await getDoc(doc(firestore, "accounting_settings", "voucher_counter"));
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  return `VOU-${String(current).padStart(5, "0")}`;
}

export async function incrementVoucherNumber() {
  const docRef = doc(firestore, "accounting_settings", "voucher_counter");
  const settingDoc = await getDoc(docRef);
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  await setDoc(docRef, { nextNumber: current + 1 }, { merge: true });
}

export async function addExpense(data) {
  const voucherNo = await getNextVoucherNumber();
  const docRef = doc(collection(firestore, "accounting_expenses"));
  await setDoc(docRef, {
    ...data,
    voucherNo,
    createdAt: new Date().toISOString(),
  });
  await incrementVoucherNumber();
  return { id: docRef.id, voucherNo };
}

export async function updateExpense(expenseId, data) {
  const docRef = doc(firestore, "accounting_expenses", expenseId);
  await updateDoc(docRef, { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteExpense(expenseId) {
  await deleteDoc(doc(firestore, "accounting_expenses", expenseId));
}

export async function getExpense(expenseId) {
  const docSnap = await getDoc(doc(firestore, "accounting_expenses", expenseId));
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
}

export async function getAllExpenses() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_expenses"), orderBy("date", "desc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getExpensesByAccount(accountId) {
  const snapshot = await getDocs(query(
    collection(firestore, "accounting_expenses"),
    where("accountId", "==", accountId),
    orderBy("date", "desc")
  ));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── ATTACH LOG TO INVOICE ──────────────────────────────────────
export async function attachLogToInvoice(invoiceId, logId, staffId, staffName) {
  const docRef = doc(firestore, "accounting_invoices", invoiceId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error("Invoice not found");
  
  const currentLogs = docSnap.data().attachedLogs || [];
  const exists = currentLogs.some(l => l.logId === logId);
  if (exists) throw new Error("Log already attached");
  
  currentLogs.push({ logId, attachedAt: new Date().toISOString(), attachedBy: staffId, attachedByName: staffName });
  await updateDoc(docRef, { attachedLogs: currentLogs });
}

export async function detachLogFromInvoice(invoiceId, logId) {
  const docRef = doc(firestore, "accounting_invoices", invoiceId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) throw new Error("Invoice not found");
  
  const currentLogs = (docSnap.data().attachedLogs || []).filter(l => l.logId !== logId);
  await updateDoc(docRef, { attachedLogs: currentLogs });
}

// ── JOURNAL VOUCHERS ──────────────────────────────────────────
export async function getNextJVNumber() {
  const settingDoc = await getDoc(doc(firestore, "accounting_settings", "jv_counter"));
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  return `JV-${String(current).padStart(5, "0")}`;
}

export async function incrementJVNumber() {
  const docRef = doc(firestore, "accounting_settings", "jv_counter");
  const settingDoc = await getDoc(docRef);
  const current = settingDoc.exists() ? settingDoc.data().nextNumber || 1 : 1;
  await setDoc(docRef, { nextNumber: current + 1 }, { merge: true });
}

export async function addJournalVoucher(data) {
  const jvNo = await getNextJVNumber();
  const docRef = doc(collection(firestore, "accounting_jv"));
  await setDoc(docRef, {
    ...data,
    jvNo,
    createdAt: new Date().toISOString(),
  });
  await incrementJVNumber();
  return { id: docRef.id, jvNo };
}

export async function getAllJournalVouchers() {
  const snapshot = await getDocs(query(collection(firestore, "accounting_jv"), orderBy("date", "desc")));
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteJournalVoucher(jvId) {
  await deleteDoc(doc(firestore, "accounting_jv", jvId));
}

export async function updateJournalVoucher(jvId, data) {
  const docRef = doc(firestore, "accounting_jv", jvId);
  await updateDoc(docRef, { ...data, updatedAt: new Date().toISOString() });
}

// ── ACCOUNTING REPORTS ─────────────────────────────────────────
export async function getAccountingSummary(startDate, endDate) {
  // Get all incomes (Paid Invoices)
  const incomes = await getDocs(query(
    collection(firestore, "accounting_invoices"),
    where("status", "==", "paid"),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  ));
  
  // Get all expenses
  const expenses = await getDocs(query(
    collection(firestore, "accounting_expenses"),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  ));
  
  const totalIncome = incomes.docs.reduce((sum, d) => sum + (d.data().totalAmount || 0), 0);
  const totalExpense = expenses.docs.reduce((sum, d) => sum + (d.data().amount || 0), 0);
  
  return {
    totalIncome,
    totalExpense,
    netProfit: totalIncome - totalExpense,
    invoiceCount: incomes.size,
    expenseCount: expenses.size,
  };
}

export async function getFullFinancialHistory() {
  const [invoicesSnap, expensesSnap, jvsSnap, accountsSnap] = await Promise.all([
    getDocs(collection(firestore, "accounting_invoices")),
    getDocs(collection(firestore, "accounting_expenses")),
    getDocs(collection(firestore, "accounting_jv")),
    getDocs(collection(firestore, "accounting_accounts"))
  ]);

  return {
    invoices: invoicesSnap.docs.map(d => d.data()),
    expenses: expensesSnap.docs.map(d => d.data()),
    jvs: jvsSnap.docs.map(d => d.data()),
    accounts: accountsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
  };
}

export async function getFinancialReports(startDate, endDate) {
  const [invoicesSnap, expensesSnap, jvSnap, accountsSnap] = await Promise.all([
    getDocs(query(collection(firestore, "accounting_invoices"), where("date", ">=", startDate), where("date", "<=", endDate))),
    getDocs(query(collection(firestore, "accounting_expenses"), where("date", ">=", startDate), where("date", "<=", endDate))),
    getDocs(query(collection(firestore, "accounting_jv"), where("date", ">=", startDate), where("date", "<=", endDate))),
    getDocs(collection(firestore, "accounting_accounts"))
  ]);

  const invoices = invoicesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const expenses = expensesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const jvs = jvSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  const accounts = accountsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

  return { invoices, expenses, jvs, accounts };
}

export async function getAccountWiseReport(accountId, startDate, endDate) {
  // Get expenses for this account
  const expenses = await getDocs(query(
    collection(firestore, "accounting_expenses"),
    where("accountId", "==", accountId),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  ));
  
  // Get income entries (for income accounts)
  const incomes = await getDocs(query(
    collection(firestore, "accounting_invoices"),
    where("status", "==", "paid"),
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  ));
  
  return {
    expenses: expenses.docs.map(d => ({ id: d.id, ...d.data() })),
    totalExpense: expenses.docs.reduce((sum, d) => sum + (d.data().amount || 0), 0),
  };
}

// ── GET ALL STUDENTS FOR ASSIGNMENT (Admin) ─────────

export async function getStudentsForAssignment() {
  const all = await getAllStudents();
  // Filter only students (not staff)
  return all.filter(s => s.role === "student" || !s.id.startsWith("STAFF-"));
}

export async function bulkEnrollStudents(studentIds, courseId, courseName, feePaid = false) {
  const promises = studentIds.map(studentId => 
    enrollStudent(studentId, courseId, courseName, feePaid)
  );
  await Promise.all(promises);
}

export async function unenrollStudent(studentId, courseId) {
  await deleteDoc(doc(firestore, "students", studentId, "enrollments", courseId));
}

export async function uploadFaceSample(studentId, sampleIndex, dataUrl) {
  return dataUrl; // Direct Base64
}

// ── CERTIFICATES ───────────────────────────────────

export async function saveCertificate(certData) {
  const certRef = doc(collection(firestore, "certificates"));
  const certId = certRef.id;
  const record = {
    ...certData,
    id: certId,
    issuedAt: new Date().toISOString(),
  };
  await setDoc(certRef, record);
  return certId;
}

export async function getCertificate(certId) {
  const snap = await getDoc(doc(firestore, "certificates", certId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateCertificate(certId, certData) {
  const certRef = doc(firestore, "certificates", certId);
  await updateDoc(certRef, {
    ...certData,
    updatedAt: new Date().toISOString(),
  });
}

export async function getAllCertificates() {
  const snap = await getDocs(query(collection(firestore, "certificates"), orderBy("issuedAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── MESSAGE TEMPLATES ──────────────────────────

export async function saveMessageTemplate(name, templateData) {
  const id = name.toLowerCase().replace(/\s+/g, '-');
  await setDoc(doc(firestore, "message_templates", id), {
    name,
    ...templateData,
    updatedAt: new Date().toISOString(),
  });
}

export async function getAllMessageTemplates() {
  const snap = await getDocs(query(collection(firestore, "message_templates"), orderBy("updatedAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteMessageTemplate(id) {
  await deleteDoc(doc(firestore, "message_templates", id));
}

// ── TYPING TUTOR ──────────────────────────────

export async function saveTypingLog(userId, logData) {
  const logRef = doc(collection(firestore, "typing_logs", userId, "sessions"));
  await setDoc(logRef, {
    ...logData,
    timestamp: new Date().toISOString()
  });

  // Update profile summary for fast dashboard access
  try {
    let coll = getCollection(userId);
    let studentRef = doc(firestore, coll, userId);
    let snap = await getDoc(studentRef);
    
    // If not found, try the other collection (handle staff/student mismatch)
    if (!snap.exists()) {
      const otherColl = coll === "staff" ? "students" : "staff";
      const otherSnap = await getDoc(doc(firestore, otherColl, userId));
      if (otherSnap.exists()) {
        snap = otherSnap;
        coll = otherColl;
        studentRef = doc(firestore, coll, userId);
      }
    }

    if (snap.exists()) {
      const data = snap.data();
      const current = data.typingStats || { totalSessions: 0, avgWpm: 0, avgAccuracy: 0 };
      const newTotal = (current.totalSessions || 0) + 1;
      const newAvgWpm = Math.round(((current.avgWpm || 0) * (newTotal - 1) + logData.speedWPM) / newTotal);
      const newAvgAccuracy = Math.round(((current.avgAccuracy || 0) * (newTotal - 1) + logData.accuracy) / newTotal);
      
      await setDoc(studentRef, {
        typingStats: {
          totalSessions: newTotal,
          avgWpm: newAvgWpm,
          avgAccuracy: newAvgAccuracy,
          lastSession: {
            timestamp: new Date().toISOString(),
            lessonTitle: logData.lessonTitle,
            wpm: logData.speedWPM,
            accuracy: logData.accuracy
          }
        }
      }, { merge: true });
    } else {
      console.warn(`[TYPING] Profile not found for userId: ${userId}. Stats summary NOT updated.`);
    }
  } catch (err) {
    console.error("Error updating typing stats summary:", err);
  }
}

export async function getTypingLogs(userId) {
  try {
    const q = query(
      collection(firestore, "typing_logs", userId, "sessions"),
      orderBy("timestamp", "desc"),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn(`[TYPING] getTypingLogs with orderBy failed for ${userId}, falling back to unordered:`, err);
    // Fallback: fetch without ordering if index is missing
    const snap = await getDocs(collection(firestore, "typing_logs", userId, "sessions"));
    const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Sort locally
    return logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 50);
  }
}

export async function getAllTypingLogs() {
  // Use collectionGroup to find all typing sessions across all users
  const snap = await getDocs(collectionGroup(firestore, "sessions"));
  // Filter only those that belong to typing_logs (in case 'sessions' is used elsewhere)
  return snap.docs
    .filter(d => d.ref.path.includes("typing_logs"))
    .map(d => ({ 
      id: d.id, 
      userId: d.ref.parent.parent.id, 
      ...d.data() 
    }));
}

// ── TYPING LESSONS (Settings) ────────────────

export async function saveTypingLesson(lessonData) {
  const id = lessonData.id || doc(collection(firestore, "typing_lessons")).id;
  await setDoc(doc(firestore, "typing_lessons", id), {
    ...lessonData,
    id,
    updatedAt: new Date().toISOString()
  });
}

export async function getAllTypingLessons() {
  const snap = await getDocs(query(collection(firestore, "typing_lessons"), orderBy("updatedAt", "desc")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteTypingLesson(id) {
  await deleteDoc(doc(firestore, "typing_lessons", id));
}

export async function migrateTypingLogs(sourceUserId, targetUserId) {
  console.log(`[TYPING] Migrating logs from ${sourceUserId} to ${targetUserId}`);
  const snap = await getDocs(collection(firestore, "typing_logs", sourceUserId, "sessions"));
  const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  
  if (logs.length === 0) {
    console.warn("[TYPING] No logs found to migrate.");
    return { moved: 0 };
  }

  for (const log of logs) {
    const newLogRef = doc(collection(firestore, "typing_logs", targetUserId, "sessions"));
    await setDoc(newLogRef, { 
      ...log, 
      migratedFrom: sourceUserId,
      updatedAt: new Date().toISOString() 
    });
    // Delete old log
    await deleteDoc(doc(firestore, "typing_logs", sourceUserId, "sessions", log.id));
  }

  await recalculateAllTypingStats();
  return { moved: logs.length };
}

export async function clearAllTypingData() {
  console.log("[TYPING] Wiping all typing session data...");
  const allLogs = await getAllTypingLogs();
  for (const log of allLogs) {
    try {
      await deleteDoc(doc(firestore, "typing_logs", log.userId, "sessions", log.id));
    } catch (err) {
      console.error(`Failed to delete log ${log.id} for user ${log.userId}:`, err);
    }
  }

  // FORCE RESET everyone's summary stats in their profiles
  const allStudents = await getAllStudents();
  for (const s of allStudents) {
    try {
      const coll = getCollection(s.id);
      await updateDoc(doc(firestore, coll, s.id), {
        typingStats: { totalSessions: 0, avgWpm: 0, avgAccuracy: 0, lastSession: null }
      });
    } catch (err) {
      console.error(`Failed to reset stats for ${s.id}:`, err);
    }
  }

  return { deleted: allLogs.length };
}

export async function resetStudentTypingStats(userId) {
  const coll = getCollection(userId);
  await updateDoc(doc(firestore, coll, userId), {
    typingStats: { totalSessions: 0, avgWpm: 0, avgAccuracy: 0, lastSession: null }
  });
  return { success: true };
}

// ── DATA MIGRATION / RECALCULATION ──────────────────

export async function recalculateAllTypingStats() {
  console.log("[DEBUG] Starting typing stats recalculation...");
  const allStudents = await getAllStudents();
  console.log(`[DEBUG] Found ${allStudents.length} members in database.`);

  let allLogs = [];
  try {
    allLogs = await getAllTypingLogs();
    console.log(`[DEBUG] collectionGroup found ${allLogs.length} total logs.`);
  } catch (err) {
    console.error("[DEBUG] collectionGroup failed:", err);
  }
  
  // Group logs by userId
  const logsByUser = {};
  const unmatchedIds = new Set();
  
  allLogs.forEach(log => {
    if (!logsByUser[log.userId]) logsByUser[log.userId] = [];
    logsByUser[log.userId].push(log);
    unmatchedIds.add(log.userId);
  });

  let updatedCount = 0;

  for (const student of allStudents) {
    const userId = student.id;
    const authUid = student.firebaseAuthUid;
    
    unmatchedIds.delete(userId);
    if (authUid) unmatchedIds.delete(authUid);
    
    // Fallback: If allLogs was empty, try fetching logs for this specific student
    let logs = logsByUser[userId] || [];
    if (authUid && authUid !== userId && logsByUser[authUid]) {
      logs = [...logs, ...logsByUser[authUid]];
    }

    // If still no logs and we suspect collectionGroup failed, try direct fetch
    if (logs.length === 0) {
      try {
        const directLogs = await getTypingLogs(userId);
        if (directLogs.length > 0) {
          console.log(`[DEBUG] Found ${directLogs.length} direct logs for ${student.name} (${userId})`);
          logs = directLogs;
        } else if (authUid && authUid !== userId) {
          const authLogs = await getTypingLogs(authUid);
          if (authLogs.length > 0) {
            console.log(`[DEBUG] Found ${authLogs.length} direct logs for ${student.name} (Auth: ${authUid})`);
            logs = authLogs;
          }
        }
      } catch (err) {
        console.error(`[DEBUG] Direct fetch failed for ${student.name}:`, err);
      }
    }

    const coll = getCollection(userId);
    const studentRef = doc(firestore, coll, userId);

    try {
      if (logs.length > 0) {
        // Sort by timestamp descending
        logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        let totalWpm = 0;
        let totalAccuracy = 0;

        for (const log of logs) {
          totalWpm += (log.speedWPM || 0);
          totalAccuracy += (log.accuracy || 0);
        }

        const newTotal = logs.length;
        const newAvgWpm = Math.round(totalWpm / newTotal);
        const newAvgAccuracy = Math.round(totalAccuracy / newTotal);
        const lastSession = {
          timestamp: logs[0].timestamp,
          lessonTitle: logs[0].lessonTitle || "Unknown",
          wpm: logs[0].speedWPM || 0,
          accuracy: logs[0].accuracy || 0
        };

        await setDoc(studentRef, {
          typingStats: {
            totalSessions: newTotal,
            avgWpm: newAvgWpm,
            avgAccuracy: newAvgAccuracy,
            lastSession: lastSession
          }
        }, { merge: true });
        updatedCount++;
      } else {
        // Reset if no logs
        await setDoc(studentRef, {
          typingStats: {
            totalSessions: 0, avgWpm: 0, avgAccuracy: 0, lastSession: null
          }
        }, { merge: true });
      }
    } catch (err) {
      console.error(`Error updating typing stats for ${student.name}:`, err);
    }
  }
  
  const unmatchedResult = {};
  if (unmatchedIds.size > 0) {
    unmatchedIds.forEach(id => {
      unmatchedResult[id] = (logsByUser[id] || []).length;
    });
    console.warn("[DEBUG] Unmatched Typing Logs Found:", unmatchedResult);
  }

  console.log(`[DEBUG] Recalculation complete. Updated ${updatedCount} profiles.`);
  return { 
    updatedCount, 
    totalStudents: allStudents.length, 
    unmatched: unmatchedResult 
  };
}

